import {ChangeDetectionStrategy, Component, Inject, output, signal} from "@angular/core";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MatOption, MatSelectModule} from "@angular/material/select";
import {FormControl, ReactiveFormsModule} from "@angular/forms";
import {MarkType, TipTapEditor, TipTapEditor_DI_TOKEN} from "../../tiptap/TipTapEditor";
import {TranslateModule} from "@ngx-translate/core";
import {erTomEllerNil} from "felles-ng2/støttefunksjoner/strenger";

const debug = initDebugger('RiktekstRedigeringFontTypeVelgerKomponentKomponent');

/**
 * Skjema for å endre font-type.
 *
 * Pga vårt behov for å kunne generer PDF-dokumenter med html-formatert innhold har vi et strengt forhold til støttede fonter.
 * Se lederplan-tjenester/src/main/resources/no/systemfabrikken/lederplan/tjenester/tekstmal/maler/generertepdfdokumenter/ressurser/fonter/README.md
 * for info om hvilke vi fonter vi inkluderer font-filer for og som danner settet av støttede fonter.
 *
 */
@Component({
    selector: 'riktekst-redigering-font-type-velger',
    templateUrl: 'font-type-velger.html',
    styleUrl: 'font-type-velger.scss',
    imports: [
        MatSelectModule,
        MatOption,
        ReactiveFormsModule,
        TranslateModule
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RiktekstRedigeringFontTypeVelgerKomponentKomponent {

    aktivert = output<boolean>();

    readonly fonter = this.tilgjengeligeFonter();
    readonly standard = this.fonter[0];
    readonly aktivFont = signal('');
    readonly kontroll = new FormControl<Font | null>(null);

    constructor(@Inject(TipTapEditor_DI_TOKEN) protected editor: TipTapEditor) {
        this.kontroll.valueChanges
          .pipe(takeUntilDestroyed())
          .subscribe(font => this.setFont(font!));

        this.editor.vedTilstandEndret(() => this.lesFont())
          .subscribe(font => this.kontroll.setValue(font, {emitEvent: false})); //emitEvent:false slik at ikke //emitEvent:false slik at ikke rammevalgSkjema.valueChanges trigges.valueChanges trigges
    }

    vedMenyLukket() {
        this.aktivert.emit(false);
    }

    vedMenyAapnet() {
        this.aktivert.emit(true);
    }

    //private hjelpemetoder

    private lesFont(): Font | null {
        let fontFamily = this.editor.editor.getAttributes(MarkType.TextStyle).fontFamily;
        //dersom fontFamily ikke er angitt på aktivt utvalg i editor returnerer vi standard font
        if (erTomEllerNil(fontFamily)) return this.standard;

        //ellers forsøker vi å finne fonten blandt våre 'støttede fonter', og returnerer null dersom ingen treff
        //hvorpå aktivFont (som da vil inneholde navnet på den ikke-støttede fontFamily) vil bli vist via mat-seleect.placeholder
        this.aktivFont.set(fontFamily);
        const dekodet = this.dekodFontFamilyStreng(fontFamily);
        return this.fonter.find(font => dekodet.some(fontnavn => font.font.has(fontnavn))) ?? null;
    }

    private setFont(font: Font) {
        if (font == this.standard)
            this.editor.editor.chain().focus().unsetFontFamily().run();
        else
            this.editor.editor.chain().focus().setFontFamily(this.enkodFontFamilyStreng(font.font)).run();
    }

    private tilgjengeligeFonter():Font[] {
        return [
            {...this.fontOgFamily('Inter, Helvetica, sans-serif'), navn: 'Standard'},
            {...this.fontOgFamily('Courier Prime, Courier, monospace'), navn: 'Monospace'},
            {...this.fontOgFamily('EB Garamond, Times New Roman, serif'), navn: 'Serif'},
        ];
    }

    private fontOgFamily(font: string): { font: Set<string>, family: string } {
        return {
            font: new Set(this.dekodFontFamilyStreng(font)),
            family: font
        };
    }

    private dekodFontFamilyStreng(fontFamily: string): string[] {
        return fontFamily.split(',').map(font => font.trim());
    }

    private enkodFontFamilyStreng(fontnavn: Iterable<string>): string {
        return Array.from(fontnavn).join(', ');
    }
}

type Font = { font: Set<string>; family: string; navn: string };
